import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react'
import { useContext, useRef, useState } from 'react'
import { useNavigate } from 'react-router'

import { ConfirmButton } from '@/components/ConfirmButton'
import {
  contentTypesColumn,
  hasQuizColumn,
  inUseColumn,
  lastUpdatedColumn,
  statusColumn,
} from '@/components/CourseModuleColumns'
import { DataTable } from '@/components/DataTable'
import { SortableColumnHeader } from '@/components/DataTable/DataTable'
import { Button } from '@/components/elements/button'
import { Input } from '@/components/elements/input'
import { MultiSelect } from '@/components/MultiSelect'
import { deleteCourse as deleteCourseRecord, getCourses } from '@/services/courses.service'
import { UserStoreContext } from '@/stores/UserStore'
import { hasAnyFilterSelected, toast } from '@/utils/helpers'

const DEFAULT_FILTERS = {
  isXverseCourse: true,
  state: [],
}

const Courses = observer(() => {
  // Context
  const navigate = useNavigate()
  const { isXverseAdmin } = useContext(UserStoreContext)

  // State
  const [sorting, setSorting] = useState([{ id: 'name', desc: false }])
  const [pagination, setPagination] = useState({
    pageSize: 20,
    pageIndex: 0,
  })
  const [search, setSearch] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  // Data Processing
  const handleError = (m) => toast(m, 'error')
  const handleSuccess = (m) => toast(m, 'success')
  const queryClient = useQueryClient()
  const { data: courses, isPending } = useQuery({
    queryKey: ['courses', sorting, pagination, searchTerm, filters],
    queryFn: () =>
      getCourses(sorting, pagination, searchTerm, {
        is_xverse_course: filters.isXverseCourse,
        state: filters.state,
      }),
    placeholderData: keepPreviousData,
  })
  const { isPending: pendingDelete, mutate: deleteCourse } = useMutation({
    mutationFn: (data) => deleteCourseRecord(data.id),
    onError: (err) => handleError(err.message),
    onSuccess: () => {
      handleSuccess('Course Module deleted successfully')

      // Refresh courses list
      queryClient.invalidateQueries({ queryKey: ['courses'] })
    },
  })

  // Filter or search changes shrink the result set, so a deep page can 404
  // from the paginator. Reset to page 0 in the same handler so React batches
  // both updates into a single render.
  const resetToFirstPage = () =>
    setPagination((prev) => (prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }))

  const debounceSearch = useRef(
    _.debounce((value) => {
      setSearchTerm(value)
      resetToFirstPage()
    }, 500),
  ).current

  return (
    <>
      {/* @fixme: This is a hacky way to make the table filters responsive */}
      <div className="flex flex-row items-center gap-2">
        {(searchTerm || hasAnyFilterSelected(filters) || filters.isXverseCourse === null) && (
          <Button
            className="visible md:hidden"
            variant="outline"
            onClick={() => {
              setSearch('')
              setSearchTerm('')
              setFilters(DEFAULT_FILTERS)
              resetToFirstPage()
            }}
          >
            Clear Filters
          </Button>
        )}
        <Button
          className="visible md:hidden"
          disabled={!isXverseAdmin}
          onClick={() => navigate('/admin/courses/course-modules/new')}
        >
          Add Course Module
        </Button>
      </div>

      <div className="flex w-full flex-row items-center justify-between">
        <div className="mt-2 mb-2 flex w-full flex-col items-center gap-4 py-1 md:flex-row">
          <Input
            className="w-full md:w-64"
            placeholder="Search for Course Module"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              debounceSearch(e.target.value)
            }}
          />
          <MultiSelect
            placeholder="Filter by Status"
            options={[
              { label: 'Draft', id: 'Draft' },
              { label: 'Published', id: 'Published' },
              { label: 'Archived', id: 'Archived' },
            ]}
            onChange={(value) => {
              setFilters((prev) => ({
                ...prev,
                state: value || [],
              }))
              resetToFirstPage()
            }}
            value={filters.state}
            width="w-full md:w-56"
          />
          {(searchTerm || hasAnyFilterSelected(filters)) && (
            <Button
              className="hidden md:block"
              variant="outline"
              onClick={() => {
                setSearch('')
                setSearchTerm('')
                setFilters(DEFAULT_FILTERS)
                resetToFirstPage()
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        <Button
          className="hidden md:block"
          disabled={!isXverseAdmin}
          onClick={() => navigate('/admin/courses/course-modules/new')}
        >
          Add Course Module
        </Button>
      </div>

      <DataTable
        columns={[
          {
            accessorKey: 'name',
            header: ({ column }) => <SortableColumnHeader column={column} label="Name" />,
            cell: ({ row }) => (
              <button
                type="button"
                className="text-action cursor-pointer text-left hover:underline"
                disabled={!isXverseAdmin}
                onClick={() => navigate(`/admin/courses/course-modules/${row.original.id}`)}
              >
                {row.original.name}
              </button>
            ),
          },
          statusColumn,
          contentTypesColumn,
          hasQuizColumn,
          inUseColumn,
          lastUpdatedColumn,
          {
            id: 'edit',
            cell: ({ row }) => (
              <Button
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="outline"
                size="sm"
                onClick={() => navigate(`/admin/courses/course-modules/${row.original.id}`)}
              >
                <Pencil className="mr-1 h-4 w-4" />
                Edit
              </Button>
            ),
            size: 100,
          },
          {
            id: 'delete',
            cell: ({ row }) => (
              <ConfirmButton
                confirmContent={`Are you sure you want to delete the course module: ${row.original.name}?`}
                confirmTitle="Delete Course Module"
                className="place-self-end"
                disabled={!isXverseAdmin}
                variant="destructive"
                size="sm"
                loading={pendingDelete}
                onClick={() => deleteCourse(row.original)}
              >
                <Trash2 className="h-4 w-4" />
                <span className="sr-only">Delete course module</span>
              </ConfirmButton>
            ),
            size: 80,
          },
        ]}
        data={courses?.results || []}
        loading={isPending}
        pagination={pagination}
        rowCount={courses?.count || 0}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
      />
    </>
  )
})

export default Courses
